/**
 * `offline_punches` store — the offline punch queue (API-4378).
 *
 * Mirrors the iOS offline punch queue and the existing `sync_offline` API
 * payload shape exactly: append-only, no per-punch status, retry count, or
 * idempotency key. The sync engine (API-4379) reads and clears the whole
 * queue as a single batch after a confirmed successful (2xx) response.
 *
 * `addPunch` and `clearAllPunches` throw on failure — losing a punch record
 * (or failing to clear a synced queue, which risks duplicate submission on
 * the next sync) must be surfaced to the caller. Reads (`getAllPunches`,
 * `getCount`) fail soft and return an empty result, since the worst case is
 * degraded offline UX (no punches to sync yet / no count to show) rather
 * than data loss.
 */

import {
    OFFLINE_PUNCHES_STORE,
    COMPANY_ID_INDEX,
    addRecord,
    getAllByIndex,
    countByIndex,
    deleteAllByIndex,
    deleteRecords
} from './offlineDb';

export interface OfflinePunch {
    id?: number; // auto-increment key, absent until persisted
    company_id: number;
    location_hash: string;
    employee_id: string; // raw PIN entered — not validated until sync
    time: string; // ISO 8601 timestamp (device time at punch)
    role_id: number | null;
    department_id: number | null;
    tips: null; // tips not supported offline
}

// Piggyback registry (same pattern as NetworkStatusContext's
// `reportNetworkError`): lets the sync engine (API-4379) react to queue
// writes without polling. Fired after a successful `addPunch`/`clearAllPunches`.
const queueChangedListeners = new Set<() => void>();

/** Subscribes to queue changes (a punch added or the queue cleared). */
export function subscribeQueueChanged(listener: () => void): () => void {
    queueChangedListeners.add(listener);
    return () => {
        queueChangedListeners.delete(listener);
    };
}

function notifyQueueChanged(): void {
    // Isolate each listener: a throwing subscriber must not stop the
    // remaining listeners from running, nor propagate back into
    // `addPunch`/`clearAllPunches`/`clearPunches` and make an already
    // successful DB write/delete look like it failed.
    queueChangedListeners.forEach(listener => {
        try {
            listener();
        } catch (err) {
            console.error('[offlinePunchQueue] Queue-change listener threw', err);
        }
    });
}

export async function addPunch(
    punch: Omit<OfflinePunch, 'id'>
): Promise<void> {
    await addRecord<Omit<OfflinePunch, 'id'>>(OFFLINE_PUNCHES_STORE, punch);
    notifyQueueChanged();
}

export async function getAllPunches(companyId: number): Promise<OfflinePunch[]> {
    try {
        return await getAllByIndex<OfflinePunch>(
            OFFLINE_PUNCHES_STORE,
            COMPANY_ID_INDEX,
            companyId
        );
    } catch (err) {
        console.error('[offlinePunchQueue] Failed to read queued punches', err);
        return [];
    }
}

export async function getCount(companyId: number): Promise<number> {
    try {
        return await countByIndex(OFFLINE_PUNCHES_STORE, COMPANY_ID_INDEX, companyId);
    } catch (err) {
        console.error('[offlinePunchQueue] Failed to count queued punches', err);
        return 0;
    }
}

export async function clearAllPunches(companyId: number): Promise<void> {
    await deleteAllByIndex(OFFLINE_PUNCHES_STORE, COMPANY_ID_INDEX, companyId);
    notifyQueueChanged();
}

/**
 * Deletes only the given punch ids — used after a successful sync to clear
 * exactly the uploaded snapshot, rather than the whole current queue, since
 * punches may have been added to the same company after the batch was read.
 */
export async function clearPunches(ids: number[]): Promise<void> {
    await deleteRecords(OFFLINE_PUNCHES_STORE, ids);
    notifyQueueChanged();
}
